import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { Spinner, Alert } from 'reactstrap';
import { UserContext } from '../../context/UserProvider';
import { getTactics, postCreateTactic, putUpdateTactic, deleteTactic } from '../../services/api';

import TacticList   from './components/TacticList';
import TacticEditor from './components/TacticEditor';
import FieldCanvas  from './components/FieldCanvas';
import Timeline     from './components/Timeline';
import StepStrip    from './components/StepStrip';
import { compileSteps, withCompiledSteps, isStepTactic, isOpponent, PASS_DURATION } from './stepCompiler';

import './Coach.css';

const GOAL_POS = { x: 0, y: 11.5 };   // shots always target the top goal (same as the engines)
const HISTORY_LIMIT = 100;

// Rival players for drills (always stored after our own players)
const MAX_OPPONENTS  = 4;
const OPPONENT_COLOR = '#e03131';
const OPPONENT_SPOTS = {
    offense: [[-3, 6], [3, 6], [0, 9.5], [-5.5, 9]],     // defending the goal we attack
    defense: [[-3, -2], [3, -2], [0, -6], [5.5, -5]],    // attacking the goal we defend
};

// ─── Default formations ───────────────────────────────────────────────────────
const DEFAULT_FORMATION_OFFENSE = [
    { index: 0, role: 'goalie', label: 'POR',  color: '#ff8800', keyframes: [{ t: 0, x: 0,   y: -13.5 }] },
    { index: 1, role: 'field',  label: 'DEF1', color: '#2266ff', keyframes: [{ t: 0, x: -4,  y: -8    }] },
    { index: 2, role: 'field',  label: 'DEF2', color: '#2266ff', keyframes: [{ t: 0, x: 4,   y: -8    }] },
    { index: 3, role: 'field',  label: 'DEL1', color: '#2266ff', keyframes: [{ t: 0, x: -3,  y: -2    }] },
    { index: 4, role: 'field',  label: 'DEL2', color: '#2266ff', keyframes: [{ t: 0, x: 3,   y: -2    }] },
];

const DEFAULT_FORMATION_DEFENSE = [
    { index: 0, role: 'goalie', label: 'POR',  color: '#ff8800', keyframes: [{ t: 0, x: 0,   y: 13.5  }] },
    { index: 1, role: 'field',  label: 'DEF1', color: '#2266ff', keyframes: [{ t: 0, x: -4,  y: 8     }] },
    { index: 2, role: 'field',  label: 'DEF2', color: '#2266ff', keyframes: [{ t: 0, x: 4,   y: 8     }] },
    { index: 3, role: 'field',  label: 'DEL1', color: '#2266ff', keyframes: [{ t: 0, x: -3,  y: 2     }] },
    { index: 4, role: 'field',  label: 'DEL2', color: '#2266ff', keyframes: [{ t: 0, x: 3,   y: 2     }] },
];

function makeNewTactic(type = 'offense') {
    const formation = type === 'defense' ? DEFAULT_FORMATION_DEFENSE : DEFAULT_FORMATION_OFFENSE;
    return {
        id:       null,
        name:     '',
        type,
        duration: 10,
        players:  formation.map(p => ({
            ...p,
            keyframes: p.keyframes.map(kf => ({ ...kf })),
        })),
        actions:  [],
        steps:    [],
    };
}

const hasSteps = t => !!t && Array.isArray(t.steps);
const stepsHaveContent = steps => (steps || []).some(s => (s.moves && s.moves.length) || s.action);

// Linear keyframe interpolation (same as the 2D engine)
function interpolate(keyframes, t) {
    if (!keyframes || keyframes.length === 0) return { x: 0, y: 0 };
    if (t <= keyframes[0].t) return { x: keyframes[0].x, y: keyframes[0].y };
    for (let i = 0; i < keyframes.length - 1; i++) {
        const a = keyframes[i], b = keyframes[i + 1];
        if (t >= a.t && t <= b.t) {
            const span = b.t - a.t;
            const k = span < 1e-4 ? 0 : (t - a.t) / span;
            return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k };
        }
    }
    const last = keyframes[keyframes.length - 1];
    return { x: last.x, y: last.y };
}

// Normalize a tactic loaded from the server and pick the mode it should open in
function prepareTactic(raw) {
    const t = JSON.parse(JSON.stringify(raw));
    if (hasSteps(t) && t.steps.length > 0) {
        t.steps = t.steps.map(s => ({ moves: s.moves || [], action: s.action || null }));
        return { tactic: withCompiledSteps(t, t.steps), mode: 'steps', step: 0 };
    }
    if (isStepTactic(t)) return { tactic: { ...t, steps: [] }, mode: 'setup', step: -1 };
    return { tactic: { ...t, steps: null }, mode: 'advanced', step: -1 };
}

// ─── Coach page ───────────────────────────────────────────────────────────────
export function Coach() {
    const { user } = useContext(UserContext);

    const [tactics,             setTactics]             = useState([]);
    const [activeTactic,        setActiveTactic]        = useState(null);
    const [currentTime,         setCurrentTime]         = useState(0);
    const [isPlaying,           setIsPlaying]           = useState(false);
    const [playEnd,             setPlayEnd]             = useState(0);
    const [loading,             setLoading]             = useState(false);
    const [saving,              setSaving]              = useState(false);
    const [error,               setError]               = useState(null);
    const [editorMode,          setEditorMode]          = useState('setup');        // 'setup' | 'steps' | 'advanced'
    const [currentStep,         setCurrentStep]         = useState(-1);             // -1 = formation
    const [activeTool,          setActiveTool]          = useState('mover');        // 'mover' | 'pase' | 'tiro' (advanced)
    const [selectedPlayerIndex, setSelectedPlayerIndex] = useState(null);
    const [isFullscreen,        setIsFullscreen]        = useState(false);
    const [copyFeedback,        setCopyFeedback]        = useState(false);

    // ─── Undo / redo ──────────────────────────────────────────────────────────
    const tacticRef  = useRef(null);
    const pastRef    = useRef([]);
    const futureRef  = useRef([]);
    const [, setHistoryVersion] = useState(0);

    const loadTactic = useCallback((t, mode, step) => {
        pastRef.current = [];
        futureRef.current = [];
        setHistoryVersion(v => v + 1);
        tacticRef.current = t;
        setActiveTactic(t);
        setIsPlaying(false);
        setCurrentTime(0);
        setEditorMode(mode);
        setCurrentStep(step);
        setActiveTool('mover');
        setSelectedPlayerIndex(null);
    }, []);

    // Every edit goes through here so it can be undone
    const applyChange = useCallback((fn, record = true) => {
        const prev = tacticRef.current;
        if (!prev) return;
        const next = fn(prev);
        if (!next || next === prev) return;
        if (record) {
            pastRef.current.push(prev);
            if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
            futureRef.current = [];
            setHistoryVersion(v => v + 1);
        }
        tacticRef.current = next;
        setActiveTactic(next);
    }, []);

    // Keep the editing mode consistent with a tactic restored by undo/redo
    const restore = useCallback((t) => {
        tacticRef.current = t;
        setActiveTactic(t);
        setHistoryVersion(v => v + 1);
        setIsPlaying(false);
        if (hasSteps(t)) {
            setEditorMode(m => (m === 'advanced' ? 'steps' : m));
            setCurrentStep(s => Math.min(s, t.steps.length - 1));
        } else {
            setEditorMode(m => (m === 'steps' ? 'advanced' : m));
        }
    }, []);

    const undo = useCallback(() => {
        if (!pastRef.current.length) return;
        futureRef.current.push(tacticRef.current);
        restore(pastRef.current.pop());
    }, [restore]);

    const redo = useCallback(() => {
        if (!futureRef.current.length) return;
        pastRef.current.push(tacticRef.current);
        restore(futureRef.current.pop());
    }, [restore]);

    useEffect(() => {
        const onKey = (e) => {
            if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            e.preventDefault();
            if (e.shiftKey) redo(); else undo();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [undo, redo]);

    // ─── Compiled step timeline ───────────────────────────────────────────────
    const compiled = useMemo(
        () => (hasSteps(activeTactic) ? compileSteps(activeTactic.players, activeTactic.steps, activeTactic.puckStart ?? 0) : null),
        [activeTactic]
    );

    // While editing, the field shows the end of the selected step (or the formation)
    useEffect(() => {
        if (isPlaying || !compiled) return;
        if (editorMode === 'steps' && compiled.stepTimes[currentStep]) setCurrentTime(compiled.stepTimes[currentStep].end);
        else if (editorMode === 'setup') setCurrentTime(0);
    }, [compiled, currentStep, editorMode, isPlaying]);

    // ─── Load tactics on mount ────────────────────────────────────────────────
    useEffect(() => {
        if (!user) return;
        setLoading(true);
        getTactics(user.uid)
            .then(data => setTactics(Array.isArray(data) ? data : []))
            .catch(err => { console.error(err); setError('Error cargando tácticas.'); })
            .finally(() => setLoading(false));
    }, [user]);

    // ─── CRUD ─────────────────────────────────────────────────────────────────
    const handleNew = () => {
        setError(null);
        loadTactic(makeNewTactic('offense'), 'setup', -1);
    };

    const handleSelect = (tactic) => {
        const { tactic: t, mode, step } = prepareTactic(tactic);
        loadTactic(t, mode, step);
    };

    const handleShare = (tactic) => {
        const url = `${window.location.origin}/tactic/${user.uid}/${tactic.id}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000);
        });
    };

    const handleDelete = async (tactic) => {
        if (!window.confirm(`Eliminar "${tactic.name || 'Sin nombre'}"?`)) return;
        try {
            await deleteTactic(user.uid, tactic.id);
            setTactics(prev => prev.filter(t => t.id !== tactic.id));
            if (activeTactic && activeTactic.id === tactic.id) { tacticRef.current = null; setActiveTactic(null); }
        } catch (err) {
            console.error(err);
            setError('Error eliminando la táctica.');
        }
    };

    const handleSave = async () => {
        if (!activeTactic) return;
        setSaving(true);
        setError(null);
        try {
            const payload = {
                id:       activeTactic.id || null,
                userId:   user.uid,
                name:     activeTactic.name,
                type:     activeTactic.type,
                duration: activeTactic.duration,
                players:  activeTactic.players,
                actions:  activeTactic.actions,
                steps:    hasSteps(activeTactic) ? activeTactic.steps : null,
                puckStart: activeTactic.puckStart ?? 0,
            };
            if (!activeTactic.id) {
                const newId = await postCreateTactic(payload);
                const saved = { ...activeTactic, id: newId, userId: user.uid };
                tacticRef.current = saved;
                setActiveTactic(saved);
                setTactics(prev => [...prev, saved]);
            } else {
                await putUpdateTactic(payload);
                setTactics(prev => prev.map(t => t.id === activeTactic.id ? { ...activeTactic } : t));
            }
        } catch (err) {
            console.error(err);
            setError('Error guardando la táctica.');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (partial) => {
        applyChange(prev => ({ ...prev, ...partial }), false);
    };

    // ─── Mode & tool ──────────────────────────────────────────────────────────
    const handleModeChange = (newMode) => {
        const t = tacticRef.current;
        if (!t) return;
        setIsPlaying(false);
        setActiveTool('mover');
        setSelectedPlayerIndex(null);

        if (newMode === 'setup') {
            setEditorMode('setup');
            setCurrentStep(-1);
            setCurrentTime(0);
            return;
        }

        if (newMode === 'steps') {
            if (!hasSteps(t)) {
                const ok = window.confirm(
                    'Esta táctica se creó con la línea de tiempo (Avanzado).\n\n' +
                    '¿Empezar de nuevo por pasos? Se mantiene la formación inicial, pero se borran los movimientos, pases y tiros.'
                );
                if (!ok) return;
            }
            const steps = hasSteps(t) && t.steps.length ? t.steps : [{ moves: [], action: null }];
            if (!hasSteps(t) || !t.steps.length) applyChange(prev => withCompiledSteps(prev, steps));
            setEditorMode('steps');
            setCurrentStep(s => Math.max(0, Math.min(s, steps.length - 1)));
            return;
        }

        // advanced
        if (hasSteps(t)) {
            if (stepsHaveContent(t.steps)) {
                const ok = window.confirm(
                    'En Avanzado editas la línea de tiempo directamente.\n\n' +
                    'Si continúas, los pasos se convierten en línea de tiempo y ya no podrás editar esta táctica por pasos. ¿Continuar?'
                );
                if (!ok) return;
            }
            applyChange(prev => ({ ...prev, steps: null }));
        }
        setEditorMode('advanced');
        setCurrentStep(-1);
        setCurrentTime(0);
    };

    const handleToolChange = (tool) => {
        setActiveTool(tool);
        setSelectedPlayerIndex(null);
    };

    // ─── Step editing ─────────────────────────────────────────────────────────
    const updateStep = useCallback((index, fn) => {
        applyChange(prev => {
            if (!hasSteps(prev) || !prev.steps[index]) return prev;
            const steps = prev.steps.map((s, i) => (i === index ? fn(s) : s));
            return withCompiledSteps(prev, steps);
        });
    }, [applyChange]);

    const handleStepMove = useCallback((playerIndex, x, y) => {
        if (!compiled || currentStep < 0) return;
        const start = compiled.startPositions[currentStep][playerIndex];
        const backHome = Math.hypot(x - start.x, y - start.y) < 0.6;
        updateStep(currentStep, step => {
            const existing = (step.moves || []).find(m => m.playerIndex === playerIndex);
            const others = (step.moves || []).filter(m => m.playerIndex !== playerIndex);
            if (backHome) return { ...step, moves: others };
            const move = { playerIndex, x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100, speed: existing ? existing.speed : 'normal' };
            return { ...step, moves: [...others, move] };
        });
    }, [compiled, currentStep, updateStep]);

    const handlePuckDrop = useCallback((target) => {
        // Formación: choose who starts with the puck
        const targetPlayer = target.type === 'pass' && tacticRef.current ? tacticRef.current.players[target.to] : null;
        if (isOpponent(targetPlayer)) return;   // the puck only goes to our own players
        if (editorMode === 'setup') {
            if (target.type !== 'pass') return;
            applyChange(prev => {
                if ((prev.puckStart ?? 0) === target.to) return prev;
                const next = { ...prev, puckStart: target.to };
                return hasSteps(prev) ? withCompiledSteps(next, prev.steps) : next;
            });
            return;
        }
        if (!compiled || currentStep < 0) return;
        const carrier = compiled.carrierAt[currentStep];
        if (carrier < 0) return;
        if (target.type === 'pass' && target.to === carrier) return;
        updateStep(currentStep, step => ({
            ...step,
            action: {
                type: target.type,
                from: carrier,
                to: target.type === 'pass' ? target.to : null,
                timing: step.action ? step.action.timing : 'start',
            },
        }));
    }, [compiled, currentStep, updateStep, editorMode, applyChange]);

    const handleMoveEdit = useCallback((playerIndex, patch) => {
        updateStep(currentStep, step => ({
            ...step,
            moves: patch
                ? step.moves.map(m => (m.playerIndex === playerIndex ? { ...m, ...patch } : m))
                : step.moves.filter(m => m.playerIndex !== playerIndex),
        }));
    }, [currentStep, updateStep]);

    const handleActionEdit = useCallback((patch, stepIndex = currentStep) => {
        updateStep(stepIndex, step => ({
            ...step,
            action: patch && step.action ? { ...step.action, ...patch } : null,
        }));
    }, [currentStep, updateStep]);

    // ─── Rival players (Formación) ─────────────────────────────────────────────
    const opponentCount = activeTactic ? activeTactic.players.filter(isOpponent).length : 0;

    const addOpponent = () => applyChange(prev => {
        const n = prev.players.filter(isOpponent).length;
        if (n >= MAX_OPPONENTS) return prev;
        const [x, y] = OPPONENT_SPOTS[prev.type === 'defense' ? 'defense' : 'offense'][n];
        const player = {
            index: prev.players.length, role: 'field', team: 'opponent',
            label: `R${n + 1}`, color: OPPONENT_COLOR, keyframes: [{ t: 0, x, y }],
        };
        const next = { ...prev, players: [...prev.players, player] };
        return hasSteps(prev) ? withCompiledSteps(next, prev.steps) : next;
    });

    const removeOpponent = () => applyChange(prev => {
        let idx = -1;
        prev.players.forEach((p, i) => { if (isOpponent(p)) idx = i; });
        if (idx < 0) return prev;
        const remap = i => (i > idx ? i - 1 : i);
        const players = prev.players.filter((_, i) => i !== idx).map((p, i) => ({ ...p, index: i }));
        const actions = (prev.actions || [])
            .filter(a => a.fromPlayerIndex !== idx && a.toPlayerIndex !== idx)
            .map(a => ({ ...a, fromPlayerIndex: remap(a.fromPlayerIndex), toPlayerIndex: a.toPlayerIndex == null ? null : remap(a.toPlayerIndex) }));
        const next = { ...prev, players, actions };
        if (!hasSteps(prev)) return next;
        const steps = prev.steps.map(s => ({
            ...s,
            moves: (s.moves || []).filter(m => m.playerIndex !== idx).map(m => ({ ...m, playerIndex: remap(m.playerIndex) })),
        }));
        return withCompiledSteps(next, steps);
    });

    const selectStep = (i) => {
        setIsPlaying(false);
        if (i < 0) { handleModeChange('setup'); return; }
        setEditorMode('steps');
        setCurrentStep(i);
    };

    const addStep = () => {
        const t = tacticRef.current;
        if (!hasSteps(t)) return;
        const at = editorMode === 'steps' && currentStep >= 0 ? currentStep + 1 : t.steps.length;
        applyChange(prev => {
            const steps = [...prev.steps];
            steps.splice(at, 0, { moves: [], action: null });
            return withCompiledSteps(prev, steps);
        });
        setIsPlaying(false);
        setEditorMode('steps');
        setCurrentStep(at);
    };

    const deleteStep = (i) => {
        const t = tacticRef.current;
        if (!hasSteps(t)) return;
        applyChange(prev => withCompiledSteps(prev, prev.steps.filter((_, j) => j !== i)));
        const remaining = t.steps.length - 1;
        if (remaining <= 0) handleModeChange('setup');
        else setCurrentStep(Math.min(i, remaining - 1));
    };

    const playAll = () => {
        if (!compiled || !compiled.stepTimes.length) return;
        setCurrentTime(0);
        setPlayEnd(compiled.stepTimes[compiled.stepTimes.length - 1].end);
        setIsPlaying(true);
    };

    const playStep = () => {
        if (!compiled || !compiled.stepTimes[currentStep]) return;
        setCurrentTime(compiled.stepTimes[currentStep].start);
        setPlayEnd(compiled.stepTimes[currentStep].end);
        setIsPlaying(true);
    };

    // What the 2D engine draws on top of the field for the selected step
    const stepView = useMemo(() => {
        if (isPlaying || !activeTactic) return null;
        if (editorMode === 'setup') {
            // Only the puck is interactive on top of the formation
            const start = activeTactic.players[activeTactic.puckStart] ? activeTactic.puckStart : 0;
            return { moves: [], prevArrows: [], action: null, carrierIndex: start, starts: [] };
        }
        if (editorMode !== 'steps' || !compiled) return null;
        const k = currentStep;
        const step = activeTactic.steps[k];
        if (!step) return null;
        const starts = compiled.startPositions[k];
        const players = compiled.players;

        const moves = (step.moves || [])
            .filter(m => players[m.playerIndex])
            .map(m => ({ playerIndex: m.playerIndex, from: starts[m.playerIndex], to: { x: m.x, y: m.y }, speed: m.speed || 'normal' }));
        const prevArrows = [];
        for (let j = Math.max(0, k - 2); j < k; j++) {
            (activeTactic.steps[j].moves || []).forEach(m => {
                if (!players[m.playerIndex]) return;
                prevArrows.push({ playerIndex: m.playerIndex, from: compiled.startPositions[j][m.playerIndex], to: { x: m.x, y: m.y } });
            });
        }

        let action = null;
        const compiledAction = compiled.actions.find(a => a.step === k);
        if (compiledAction) {
            const from = interpolate(players[compiledAction.fromPlayerIndex].keyframes, compiledAction.t);
            const to = compiledAction.type === 'pass'
                ? interpolate(players[compiledAction.toPlayerIndex].keyframes, compiledAction.t + PASS_DURATION)
                : GOAL_POS;
            action = { type: compiledAction.type, from, to };
        }
        return { moves, prevArrows, action, carrierIndex: compiled.carrierAt[k], starts };
    }, [editorMode, isPlaying, compiled, activeTactic, currentStep]);

    const stepHint = (() => {
        if (!compiled || currentStep < 0) return '';
        const carrier = compiled.carrierAt[currentStep];
        const move = 'Arrastra jugadores a su siguiente posición';
        const puck = carrier >= 0 ? ' · Arrastra el disco a un compañero (pase) o a la portería (tiro)' : '';
        return `${move}${puck} · Toca una flecha para cambiar velocidad o quitarla`;
    })();

    // ─── Player drag → formation / step move / keyframe ───────────────────────
    const handlePlayerDrag = useCallback((playerIndex, x, y) => {
        if (editorMode === 'steps') { handleStepMove(playerIndex, x, y); return; }
        applyChange(prev => {
            if (editorMode === 'setup' && hasSteps(prev)) {
                // Formation change: everything after it is recompiled from the steps
                const players = prev.players.map((p, i) => (i === playerIndex ? { ...p, keyframes: [{ t: 0, x, y }] } : p));
                return withCompiledSteps({ ...prev, players }, prev.steps);
            }
            const t = editorMode === 'setup' ? 0 : currentTime;
            const players = prev.players.map((p, i) => {
                if (i !== playerIndex) return p;
                // Upsert: remove any keyframe within 0.1s, insert new one
                const filtered = p.keyframes.filter(kf => Math.abs(kf.t - t) > 0.1);
                const updated  = [...filtered, { t, x, y }].sort((a, b) => a.t - b.t);
                return { ...p, keyframes: updated };
            });
            return { ...prev, players };
        });
    }, [editorMode, currentTime, applyChange, handleStepMove]);

    // ─── Keyframe handle drag → reposition existing keyframe (advanced) ───────
    const handleKeyframeDrag = useCallback((playerIndex, oldT, newX, newY) => {
        applyChange(prev => {
            const players = prev.players.map((p, i) => {
                if (i !== playerIndex) return p;
                const updated = p.keyframes.map(kf =>
                    Math.abs(kf.t - oldT) <= 0.05 ? { ...kf, x: newX, y: newY } : kf
                );
                return { ...p, keyframes: updated };
            });
            return { ...prev, players };
        });
    }, [applyChange]);

    // ─── Pass / shot drawn on canvas (advanced) ───────────────────────────────
    const handlePassDraw = useCallback((fromIndex, toIndex) => {
        applyChange(prev => {
            const action = { type: 'pass', fromPlayerIndex: fromIndex, toPlayerIndex: toIndex, t: currentTime };
            return { ...prev, actions: [...(prev.actions || []), action] };
        });
    }, [currentTime, applyChange]);

    const handleShotDraw = useCallback((fromIndex) => {
        applyChange(prev => {
            const action = { type: 'shoot', fromPlayerIndex: fromIndex, toPlayerIndex: null, t: currentTime };
            return { ...prev, actions: [...(prev.actions || []), action] };
        });
    }, [currentTime, applyChange]);

    // ─── Delete action (advanced list) ────────────────────────────────────────
    const handleDeleteAction = (idx) => {
        applyChange(prev => ({ ...prev, actions: (prev.actions || []).filter((_, i) => i !== idx) }));
    };

    // ─── Delete a single keyframe (advanced) ──────────────────────────────────
    const handleDeleteKeyframe = useCallback((playerIndex, kfT) => {
        applyChange(prev => {
            const players = prev.players.map((p, i) => {
                if (i !== playerIndex) return p;
                // Must keep at least 1 keyframe (the t=0 starting position)
                const filtered = p.keyframes.filter(kf => Math.abs(kf.t - kfT) > 0.05);
                return { ...p, keyframes: filtered.length > 0 ? filtered : p.keyframes };
            });
            return { ...prev, players };
        });
    }, [applyChange]);

    // ─── Shared canvas + bottom panel props ──────────────────────────────────
    const stepEditing = editorMode === 'steps';
    const canvasProps = {
        tactic:              activeTactic,
        currentTime,
        editorMode,
        activeTool,
        selectedPlayerIndex: stepEditing ? null : selectedPlayerIndex,
        onPlayerDrag:        handlePlayerDrag,
        onPlayerSelect:      setSelectedPlayerIndex,
        onPassDraw:          handlePassDraw,
        onShotDraw:          handleShotDraw,
        onKeyframeDrag:      handleKeyframeDrag,
        onDeleteKeyframe:    handleDeleteKeyframe,
        onModeChange:        handleModeChange,
        onToolChange:        handleToolChange,
        onToggleFullscreen:  () => setIsFullscreen(fs => !fs),
        stepView,
        currentStep:         stepEditing && activeTactic && hasSteps(activeTactic) ? activeTactic.steps[currentStep] : null,
        stepHint,
        onPuckDrop:          handlePuckDrop,
        onMoveEdit:          handleMoveEdit,
        onActionEdit:        handleActionEdit,
        opponentCount,
        maxOpponents:        MAX_OPPONENTS,
        onAddOpponent:       addOpponent,
        onRemoveOpponent:    removeOpponent,
    };

    const timelineProps = {
        currentTime,
        duration:         activeTactic ? activeTactic.duration : 10,
        isPlaying,
        isFullscreen,
        editorMode,
        tactic:           activeTactic,
        selectedPlayerIndex,
        onTimeChange:     setCurrentTime,
        onPlay:           () => setIsPlaying(true),
        onPause:          () => setIsPlaying(false),
        onDurationChange: d => applyChange(prev => ({ ...prev, duration: d })),
        onActionClick:    t => { setIsPlaying(false); setCurrentTime(t); },
    };

    const bottomPanel = activeTactic && hasSteps(activeTactic) && compiled ? (
        <StepStrip
            steps={activeTactic.steps}
            stepTimes={compiled.stepTimes}
            invalidSteps={compiled.invalidSteps}
            currentStep={editorMode === 'steps' ? currentStep : -1}
            currentTime={currentTime}
            isPlaying={isPlaying}
            playEnd={playEnd}
            canUndo={pastRef.current.length > 0}
            canRedo={futureRef.current.length > 0}
            onSelectStep={selectStep}
            onAddStep={addStep}
            onDeleteStep={deleteStep}
            onPlayAll={playAll}
            onPlayStep={playStep}
            onPause={() => setIsPlaying(false)}
            onTimeChange={setCurrentTime}
            onPlayEnd={() => setIsPlaying(false)}
            onUndo={undo}
            onRedo={redo}
        />
    ) : (
        <Timeline {...timelineProps} />
    );

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            {/* Fullscreen overlay */}
            {isFullscreen && (
                <div className="coach-fullscreen-overlay">
                    <div className="field-canvas-container" style={{ flex: 1, minHeight: 0 }}>
                        <FieldCanvas {...canvasProps} isFullscreen={true} />
                    </div>
                    <div className="coach-timeline-panel" style={{ flexShrink: 0 }}>
                        {bottomPanel}
                    </div>
                </div>
            )}

            <div className="coach-layout">
                {/* Sidebar */}
                <div className="coach-sidebar">
                    <h6 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Tácticas</h6>
                    {loading && <Spinner size="sm" />}
                    {copyFeedback && (
                        <Alert color="success" style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                            ¡Enlace copiado!
                        </Alert>
                    )}
                    <TacticList
                        tactics={tactics}
                        activeTactic={activeTactic}
                        onSelect={handleSelect}
                        onNew={handleNew}
                        onShare={handleShare}
                        onDelete={handleDelete}
                    />
                </div>

                {/* Main area */}
                <div className="coach-main">
                    {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

                    {/* Editor panel */}
                    <div className="coach-editor-panel">
                        <TacticEditor
                            tactic={activeTactic}
                            saving={saving}
                            editorMode={editorMode}
                            activeTool={activeTool}
                            stepActions={compiled ? compiled.actions : null}
                            onChange={handleChange}
                            onSave={handleSave}
                            onDeleteAction={handleDeleteAction}
                            onDeleteStepAction={stepIndex => handleActionEdit(null, stepIndex)}
                            onSelectStep={selectStep}
                        />
                    </div>

                    {/* Field canvas — hidden when fullscreen overlay is active */}
                    <div className="field-canvas-container" style={isFullscreen ? { visibility: 'hidden' } : {}}>
                        {!isFullscreen && <FieldCanvas {...canvasProps} isFullscreen={false} />}
                    </div>

                    {/* Steps / timeline */}
                    <div className="coach-timeline-panel">
                        {bottomPanel}
                    </div>
                </div>
            </div>
        </>
    );
}
